import type { ChainId } from '../../sdk'
import type { AppState } from '../../state'

import {
  isMobileSwapSessionConfirmed,
  type ChainTransactions,
} from './mobileSwapRecovery'
import {
  clearMobileSwapSession,
  isStaleMobileSwapPendingSession,
  patchMobileSwapSession,
  readMobileSwapSession,
  type MobileSwapSession,
} from './mobileSwapSession'

/** Solo reanudar /swap si hay swap en curso (MetaMask / tx pendiente), no tras éxito ya cerrado. */
export function shouldResumeMobileSwapRoute(
  session: MobileSwapSession | null | undefined,
  chainTransactions?: ChainTransactions,
): boolean {
  if (!session || session.dismissed || session.phase === 'failed') return false
  if (session.phase === 'confirmed') return false
  if (isStaleMobileSwapPendingSession(session)) {
    clearMobileSwapSession()
    return false
  }

  if (session.phase === 'wallet' || session.phase === 'submitted') {
    if (session.hash && chainTransactions && isMobileSwapSessionConfirmed(session, chainTransactions, session.hash)) {
      return false
    }
    return true
  }

  if (session.hash) {
    if (chainTransactions && isMobileSwapSessionConfirmed(session, chainTransactions, session.hash)) {
      return false
    }
    return true
  }

  return false
}

export function pruneMobileSwapSessionForColdBoot(transactions?: AppState['transactions']): void {
  const session = readMobileSwapSession()
  if (!session) return

  if (session.phase === 'review' && !session.hash) {
    clearMobileSwapSession()
    return
  }

  if (isStaleMobileSwapPendingSession(session)) {
    clearMobileSwapSession()
    return
  }

  if (session.hash && transactions) {
    const chainTransactions = transactions[session.chainId as ChainId]
    if (isMobileSwapSessionConfirmed(session, chainTransactions, session.hash)) {
      if (session.phase !== 'confirmed') {
        patchMobileSwapSession({ phase: 'confirmed', hash: session.hash })
      }
    }
  }
}

/** True cuando la pestaña se recargó (F5 / pull-to-refresh), no en navegación fría. */
export function hasSwapEntryParamsInHash(): boolean {
  if (typeof window === 'undefined') return false
  const hash = window.location.hash
  const q = hash.indexOf('?')
  if (q < 0) return false
  const qs = hash.slice(q + 1)
  return /(?:^|&)(poolId|pair|token0|token1|inputCurrency|outputCurrency)=/.test(qs)
}

export function isBrowserPageReload(): boolean {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return false

  const navigationEntry = performance.getEntriesByType?.('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined
  if (navigationEntry?.type === 'reload') return true

  const legacyNavigation = (performance as Performance & { navigation?: { type?: number } }).navigation
  return legacyNavigation?.type === 1
}

export function resolveMobileSwapBootRedirect(
  pathname: string,
  resumeSwap: boolean,
  options?: { isPageReload?: boolean; allowSwapEntry?: boolean },
): '/' | '/swap' | null {
  const onSwap = pathname === '/swap' || pathname.startsWith('/swap/')
  const allowSwapRoute =
    resumeSwap || options?.isPageReload === true || options?.allowSwapEntry === true

  if (onSwap && !allowSwapRoute) return '/'
  if (!onSwap && resumeSwap && pathname === '/') return '/swap'
  return null
}
